import React from 'react';
import { cn } from '@/lib/utils';

export type SegmentedOption<T extends string> = {
  value: T;
  label: string;
  icon?: React.ReactNode;
};

type SegmentedControlProps<T extends string> = {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Stretch segments to equal widths (mobile tab rows). */
  grow?: boolean;
  size?: 'sm' | 'md';
  className?: string;
};

/**
 * The handoff's segmented control: muted pill container, active segment on a
 * `bg-background` card with a subtle shadow. Used for Details/Pricing, the
 * Open system/Event toggle and the editor tabs.
 */
export default function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  grow = false,
  size = 'md',
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      className={cn(
        'tw:inline-flex tw:gap-0.5 tw:rounded-md tw:border tw:border-border tw:bg-muted tw:p-[3px]',
        grow && 'tw:flex tw:w-full',
        className,
      )}
      role="tablist"
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'tw:inline-flex tw:items-center tw:justify-center tw:gap-1.5 tw:whitespace-nowrap tw:rounded-[6px] tw:border-none tw:font-medium tw:outline-none tw:transition-colors tw:cursor-pointer',
              size === 'md'
                ? 'tw:h-[34px] tw:px-3.5 tw:text-[13.5px]'
                : 'tw:h-7 tw:px-3 tw:text-xs',
              grow && 'tw:min-w-0 tw:flex-1',
              active
                ? 'tw:bg-background tw:font-semibold tw:text-foreground tw:shadow-xs'
                : 'tw:bg-transparent tw:text-muted-foreground tw:hover:text-foreground',
            )}
          >
            {option.icon}
            <span className="tw:truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
